import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

// Locally-bundled full-body humanoid GLB with ARKit face blendshapes +
// Oculus visemes + a rigged body. Served from /avatar-body.glb so the avatar
// works in any environment, regardless of external CDN reachability.
const DEFAULT_AVATAR_URL = '/avatar-body.glb';

// ARKit morph targets we drive for emotion & blinks.
const SMILE_KEYS = ['mouthSmileLeft', 'mouthSmileRight'];
const BLINK_KEYS = ['eyeBlinkLeft', 'eyeBlinkRight'];

/**
 * Oculus viseme code → ARKit blendshape recipe (weights in 0..1).
 * Keys map to ARKit 52 blendshape names present on the facecap GLB.
 * Each viseme is a weighted blend of up to ~4 morphs.
 */
const VISEME_RECIPES = {
  0: {}, // sil — rest mouth
  1: { mouthClose: 0.55, mouthPressLeft: 0.3, mouthPressRight: 0.3 }, // PP (p/b/m)
  2: { mouthLowerDownLeft: 0.35, mouthLowerDownRight: 0.35, mouthRollLower: 0.35 }, // FF (f/v)
  3: { jawOpen: 0.18, tongueOut: 0.45 }, // TH
  4: { jawOpen: 0.25 }, // DD (t/d)
  5: { jawOpen: 0.3 }, // kk (k/g)
  6: { mouthFunnel: 0.45, mouthPucker: 0.3, jawOpen: 0.18 }, // CH (sh/ch/j)
  7: { jawOpen: 0.12, mouthPressLeft: 0.15, mouthPressRight: 0.15 }, // SS (s/z)
  8: { jawOpen: 0.16, mouthClose: 0.3 }, // nn (n/l)
  9: { mouthPucker: 0.45, jawOpen: 0.18 }, // RR
  10: { jawOpen: 0.8 }, // aa (father)
  11: { jawOpen: 0.5, mouthSmileLeft: 0.15, mouthSmileRight: 0.15 }, // E (bed)
  12: { jawOpen: 0.28, mouthSmileLeft: 0.22, mouthSmileRight: 0.22 }, // ih (it)
  13: { jawOpen: 0.5, mouthFunnel: 0.5 }, // oh
  14: { jawOpen: 0.28, mouthPucker: 0.6, mouthFunnel: 0.4 }, // ou (boot)
};

// All ARKit morphs referenced across recipes — needed so we can reset them to
// 0 each frame before re-applying the current viseme.
const ALL_VISEME_MORPHS = [
  'jawOpen', 'mouthClose', 'mouthPressLeft', 'mouthPressRight',
  'mouthLowerDownLeft', 'mouthLowerDownRight', 'mouthRollLower',
  'mouthFunnel', 'mouthPucker', 'tongueOut',
];

function setMorph(mesh, names, value) {
  if (!mesh || !mesh.morphTargetDictionary || !mesh.morphTargetInfluences) return;
  for (const name of names) {
    const idx = mesh.morphTargetDictionary[name];
    if (idx !== undefined) mesh.morphTargetInfluences[idx] = value;
  }
}

/** Binary search the viseme index active at time `ms` within the track. */
function findVisemeIndex(vtimes, vdurations, ms) {
  if (!vtimes || !vtimes.length) return -1;
  // Fast path: scan forward from last hit would be better, but tracks are small
  let lo = 0, hi = vtimes.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const start = vtimes[mid];
    const end = start + vdurations[mid];
    if (ms < start) hi = mid - 1;
    else if (ms >= end) lo = mid + 1;
    else return mid;
  }
  return -1;
}

const TalkingAvatar = forwardRef(function TalkingAvatar(
  { avatarUrl = DEFAULT_AVATAR_URL, backgroundColor = '#1a1a2e', height = 340 },
  ref
) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const avatarRootRef = useRef(null);
  const morphMeshesRef = useRef([]);
  const boneRef = useRef({});           // name → THREE.Bone
  const gestureTrackRef = useRef(null); // [{start, end, type, side}]
  const gestureSmoothRef = useRef({ emphL: 0, emphR: 0, spine: 0, nod: 0 });
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const currentSourceRef = useRef(null);
  // Viseme track for phoneme-driven lip-sync
  const visemeTrackRef = useRef(null); // { visemes, vtimes, vdurations }
  const audioStartTimeRef = useRef(0); // AudioContext currentTime when src.start()
  // Smoothed per-morph weights so mouth transitions are fluid
  const morphWeightsRef = useRef({});
  const speakingRef = useRef(false);
  const blinkTimerRef = useRef(0);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [progress, setProgress] = useState(0);

  // Initialize scene once
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const width = el.clientWidth;
    const h = el.clientHeight;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(backgroundColor);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(28, width / h, 0.1, 100);
    camera.position.set(0, 1.3, 1.95);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setSize(width, h);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    el.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lighting
    const hemi = new THREE.HemisphereLight(0xffffff, 0x443355, 0.8);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(1.5, 2.5, 2);
    scene.add(dir);
    const rim = new THREE.DirectionalLight(0x9ec37a, 0.6);
    rim.position.set(-2, 1.5, -1.5);
    scene.add(rim);

    // Soft radial backdrop glow
    const bgGeom = new THREE.PlaneGeometry(6, 6);
    const bgMat = new THREE.MeshBasicMaterial({
      color: 0x2a2a48,
      transparent: true,
      opacity: 0.0,
    });
    const bg = new THREE.Mesh(bgGeom, bgMat);
    bg.position.set(0, 1.5, -1);
    scene.add(bg);

    // Load avatar (facecap.glb uses KTX2/basis + meshopt compression)
    const ktx2 = new KTX2Loader()
      .setTranscoderPath('/basis/')
      .detectSupport(renderer);
    const loader = new GLTFLoader()
      .setKTX2Loader(ktx2)
      .setMeshoptDecoder(MeshoptDecoder);
    loader.load(
      avatarUrl,
      (gltf) => {
        const root = gltf.scene;
        const bones = {};
        root.traverse((obj) => {
          if (obj.isMesh && obj.morphTargetDictionary) {
            morphMeshesRef.current.push(obj);
          }
          if (obj.isMesh) {
            obj.frustumCulled = false;
          }
          if (obj.isBone || obj.type === 'Bone') {
            bones[obj.name] = obj;
          }
        });
        // Store discovered bones for the gesture system.
        boneRef.current = bones;

        // Apply a natural "arms-at-sides" rest pose. The source GLB loads in a
        // T-pose (arms stretched horizontally). Regardless of the rig's local
        // axis conventions, rotating each arm bone ~80° around the WORLD Z
        // axis brings the upper arm down alongside the torso.
        const worldZ = new THREE.Vector3(0, 0, 1);
        const worldY = new THREE.Vector3(0, 1, 0);
        const setRestFromCurrent = (b) => {
          if (b) b.userData.restRotation = { x: b.rotation.x, y: b.rotation.y, z: b.rotation.z };
        };

        const leftArm = bones['LeftArm'];
        const rightArm = bones['RightArm'];
        if (leftArm) {
          // LeftArm in T-pose points along the character's left (world -X).
          // Rotate ~80° around world +Z to bring it down to world -Y.
          leftArm.rotateOnWorldAxis(worldZ, -Math.PI * 0.44);
          // Slight inward twist so hand rests close to the hip.
          leftArm.rotateOnWorldAxis(worldY, Math.PI * 0.05);
          setRestFromCurrent(leftArm);
        }
        if (rightArm) {
          rightArm.rotateOnWorldAxis(worldZ, Math.PI * 0.44);
          rightArm.rotateOnWorldAxis(worldY, -Math.PI * 0.05);
          setRestFromCurrent(rightArm);
        }
        // Very slight natural elbow bend so arms are not ramrod-straight.
        const leftForeArm = bones['LeftForeArm'];
        const rightForeArm = bones['RightForeArm'];
        if (leftForeArm) {
          leftForeArm.rotateOnWorldAxis(worldZ, -Math.PI * 0.06);
          setRestFromCurrent(leftForeArm);
        }
        if (rightForeArm) {
          rightForeArm.rotateOnWorldAxis(worldZ, Math.PI * 0.06);
          setRestFromCurrent(rightForeArm);
        }
        // Capture rest on other gesture-relevant bones too.
        for (const n of ['Spine', 'Spine1', 'Spine2', 'Neck', 'Head', 'LeftShoulder', 'RightShoulder', 'LeftHand', 'RightHand']) {
          setRestFromCurrent(bones[n]);
        }

        // Auto-frame for a full-body humanoid GLB. We want an "upper body"
        // framing so the face (lip-sync) and shoulders/chest (breathing sway)
        // are both clearly visible.
        const bbox = new THREE.Box3().setFromObject(root);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        bbox.getSize(size);
        bbox.getCenter(center);

        const bodyHeight = Math.max(size.y, 0.01); // e.g. ~1.7 for human-scale
        // Normalize avatar so a full body is about 1.7 units tall. This keeps
        // camera math stable regardless of the source model's scale.
        const scale = 1.7 / bodyHeight;
        root.scale.setScalar(scale);

        // Recenter around origin (feet at ~0, head ~1.7).
        const scaledCenter = new THREE.Vector3().copy(center).multiplyScalar(scale);
        root.position.sub(scaledCenter);
        // Shift so the feet sit below the frame; camera will look at chest level.
        root.position.y += (bodyHeight * scale) / 2;

        // Head top is now at ~1.7. Frame so head is centered vertically in the
        // viewport, with shoulders + chest visible below.
        const headY = 1.55;        // approx eye/forehead height
        const chestY = 1.30;       // approx chest height — camera look-at target
        const camera = cameraRef.current;
        camera.fov = 24;           // narrower FOV for a portrait-style composition
        camera.position.set(0, chestY + 0.05, 1.95);
        camera.lookAt(0, chestY, 0);
        camera.updateProjectionMatrix();

        // Stash for animation-loop use
        root.userData.headY = headY;
        root.userData.chestY = chestY;

        scene.add(root);
        avatarRootRef.current = root;
        setStatus('ready');
      },
      (ev) => {
        if (ev && ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
      },
      (err) => {
        console.error('[TalkingAvatar] GLTF load error', err);
        setStatus('error');
      }
    );

    // Animation loop — uses THREE.Timer (the modern replacement for Clock).
    const timer = new THREE.Timer();
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      timer.update();
      const delta = timer.getDelta();
      const t = timer.getElapsed();

      // Idle breathing + subtle sway on the whole rig
      const root = avatarRootRef.current;
      if (root) {
        const sway = Math.sin(t * 0.6) * 0.03;
        const breathe = Math.sin(t * 1.3) * 0.008; // ~78 bpm breathing
        root.rotation.y = sway;
        // Small vertical "breathing" on the root — cheap but readable as life
        root.scale.y = (root.userData.baseScaleY = root.userData.baseScaleY || root.scale.y) * (1 + breathe);
      }

      // ── Phoneme-driven lip-sync ─────────────────────────────────────────
      // Compute target weights for every viseme morph, then smooth toward them.
      const targetWeights = {};
      for (const m of ALL_VISEME_MORPHS) targetWeights[m] = 0;

      let rmsGain = 0;
      if (speakingRef.current && analyserRef.current) {
        const data = analyserRef.current._buf;
        analyserRef.current.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / data.length);
        rmsGain = Math.min(1, rms * 4.5); // 0 during silence, ~1 during loud speech
      }

      if (speakingRef.current && visemeTrackRef.current && audioCtxRef.current) {
        const track = visemeTrackRef.current;
        const elapsedMs = (audioCtxRef.current.currentTime - audioStartTimeRef.current) * 1000;
        const idx = findVisemeIndex(track.vtimes, track.vdurations, elapsedMs);
        if (idx >= 0) {
          const visCode = track.visemes[idx];
          const recipe = VISEME_RECIPES[visCode] || {};
          // Scale viseme intensity by audio RMS so silences collapse the mouth.
          // Floor at 0.35 so clearly-voiced phonemes still register on quiet words.
          const intensity = Math.max(0.35, rmsGain);
          for (const [morph, weight] of Object.entries(recipe)) {
            targetWeights[morph] = weight * intensity;
          }
        }
      }

      // Exponential smoothing toward target weights (separate, fast attack)
      const weights = morphWeightsRef.current;
      const smooth = Math.min(1, delta * 22); // ~45ms time-constant
      for (const m of ALL_VISEME_MORPHS) {
        const prev = weights[m] || 0;
        const tgt = targetWeights[m] || 0;
        weights[m] = prev + (tgt - prev) * smooth;
      }

      // Apply to all morph-capable meshes
      const meshes = morphMeshesRef.current;
      for (const mesh of meshes) {
        for (const m of ALL_VISEME_MORPHS) {
          setMorph(mesh, [m], weights[m]);
        }
        // Subtle smile while idle, neutral while speaking so mouth shapes read
        const smileAmt = speakingRef.current ? 0.04 : 0.16 + Math.sin(t * 0.5) * 0.04;
        setMorph(mesh, SMILE_KEYS, smileAmt);
      }

      // Blink every 3-5s
      blinkTimerRef.current -= delta;
      if (blinkTimerRef.current <= 0) {
        // Run a quick blink using a transient shape
        const start = performance.now();
        const doBlink = () => {
          const elapsed = (performance.now() - start) / 1000;
          const dur = 0.18;
          let amt;
          if (elapsed < dur / 2) amt = elapsed / (dur / 2);
          else if (elapsed < dur) amt = 1 - (elapsed - dur / 2) / (dur / 2);
          else amt = 0;
          for (const mesh of meshes) setMorph(mesh, BLINK_KEYS, amt);
          if (elapsed < dur) requestAnimationFrame(doBlink);
        };
        requestAnimationFrame(doBlink);
        blinkTimerRef.current = 3 + Math.random() * 2.5;
      }

      // ── Speech-driven arm/shoulder gestures (smoothed) ─────────────────
      // Pipeline: (a) sample "raw" emphasis targets from the gesture track,
      // (b) feed through an exponential smoother so transitions are fluid,
      // (c) add an organic idle sway built from two offset sines, so the
      // avatar never loops to the same pose twice in a short window.
      const bones = boneRef.current;
      if (bones && Object.keys(bones).length) {
        const speaking = speakingRef.current;
        const talkGain = speaking ? (0.55 + rmsGain * 0.55) : 0.2;

        // Smoothstep curve for emphasis envelopes — much less twitchy than
        // the old linear triangle.
        const smoothstep = (x) => {
          const c = Math.max(0, Math.min(1, x));
          return c * c * (3 - 2 * c);
        };

        // ── 1. Raw emphasis targets from the scheduled track ─────────────
        let rawL = 0, rawR = 0, rawSpine = 0, rawNod = 0;
        if (speaking && gestureTrackRef.current && audioCtxRef.current) {
          const elapsedMs = (audioCtxRef.current.currentTime - audioStartTimeRef.current) * 1000;
          for (const g of gestureTrackRef.current) {
            if (elapsedMs < g.start || elapsedMs > g.end) continue;
            const local = (elapsedMs - g.start) / Math.max(1, g.end - g.start);
            // Smooth rise then smooth fall: peak at local=0.5
            const env = smoothstep(local < 0.5 ? local * 2 : (1 - local) * 2);
            if (g.type === 'emphasis' && g.side === 'left') rawL = env;
            else if (g.type === 'emphasis' && g.side === 'right') rawR = env;
            else if (g.type === 'both_open') { rawL = env * 0.35; rawR = env * 0.35; }
            else if (g.type === 'nod') rawNod = env;
            rawSpine = env * (g.side === 'left' ? 0.025 : -0.025);
          }
        }

        // ── 2. Exponential smoothing toward raw targets ─────────────────
        // Separate attack/release so gestures rise quickly but relax slowly,
        // which reads as "deliberate" rather than twitchy.
        const gs = gestureSmoothRef.current;
        const attack = Math.min(1, delta * 6);   // ~166ms
        const release = Math.min(1, delta * 2.5); // ~400ms
        const smooth = (prev, tgt) =>
          prev + (tgt - prev) * (tgt > prev ? attack : release);
        gs.emphL  = smooth(gs.emphL  || 0, rawL);
        gs.emphR  = smooth(gs.emphR  || 0, rawR);
        gs.spine  = smooth(gs.spine  || 0, rawSpine);
        gs.nod    = smooth(gs.nod    || 0, rawNod);
        const emphLeft = gs.emphL;
        const emphRight = gs.emphR;
        const spineTwist = gs.spine;
        const headNod = gs.nod;

        // ── 3. Apply bone deltas (smaller amplitudes than first draft) ──
        const applyAddtl = (name, dx, dy, dz) => {
          const b = bones[name];
          if (!b || !b.userData.restRotation) return;
          const r = b.userData.restRotation;
          b.rotation.x = r.x + dx;
          b.rotation.y = r.y + dy;
          b.rotation.z = r.z + dz;
        };

        // Organic two-frequency idle sway (breaks periodicity).
        const idle = (f1, f2, phase) =>
          (Math.sin(t * f1 + phase) * 0.6 + Math.sin(t * f2 + phase * 1.3) * 0.4) * talkGain;

        // A presenter at rest keeps arms at the sides; on emphasis the wrist
        // and forearm lift a modest amount (think: a gentle "open-palm"
        // illustrator gesture), while the upper arm barely moves. These
        // amplitudes are deliberately very small — ~5-10° max at the elbow.
        applyAddtl(
          'LeftArm',
          -emphLeft * 0.04,                  // almost no upper-arm motion
          emphLeft * 0.02,
          idle(0.9, 1.35, 0.0) * 0.010,
        );
        applyAddtl(
          'LeftForeArm',
          -emphLeft * 0.18,                  // subtle elbow lift on emphasis
          emphLeft * 0.10 + idle(1.05, 1.6, 0.5) * 0.025,
          emphLeft * 0.06,
        );
        applyAddtl('LeftHand', 0, emphLeft * 0.06, emphLeft * 0.08);

        // Right arm — mirror.
        applyAddtl(
          'RightArm',
          -emphRight * 0.04,
          -emphRight * 0.02,
          idle(0.9, 1.35, 1.7) * 0.010,
        );
        applyAddtl(
          'RightForeArm',
          -emphRight * 0.18,
          -emphRight * 0.10 + idle(1.05, 1.6, 2.1) * 0.025,
          -emphRight * 0.06,
        );
        applyAddtl('RightHand', 0, -emphRight * 0.06, -emphRight * 0.08);

        // Upper body: subtle spine twist + head tilt toward the emphasising hand.
        applyAddtl('Spine1', 0, spineTwist, 0);
        applyAddtl('Spine2', 0, spineTwist * 0.5, 0);
        applyAddtl('Neck', headNod * 0.12, -spineTwist * 0.25, 0);
        applyAddtl('Head',
          headNod * 0.09,
          -spineTwist * 0.3,
          Math.sin(t * 0.45) * 0.015,  // very slight head sway
        );
      }

      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const onResize = () => {
      if (!containerRef.current || !rendererRef.current || !cameraRef.current) return;
      const w = containerRef.current.clientWidth;
      const h2 = containerRef.current.clientHeight;
      rendererRef.current.setSize(w, h2);
      cameraRef.current.aspect = w / h2;
      cameraRef.current.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        if (currentSourceRef.current) currentSourceRef.current.stop();
      } catch {}
      try {
        if (audioCtxRef.current) audioCtxRef.current.close();
      } catch {}
      try {
        renderer.dispose();
      } catch {}
      if (renderer.domElement && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      morphMeshesRef.current = [];
      avatarRootRef.current = null;
      rendererRef.current = null;
      sceneRef.current = null;
      cameraRef.current = null;
    };
  }, [avatarUrl, backgroundColor]);

  /** Decode base64 mp3 into an AudioBuffer. */
  async function decodeBase64Mp3(audioCtx, base64) {
    const bin = atob(base64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return await audioCtx.decodeAudioData(bytes.buffer);
  }

  /** Build a sparse gesture track from word timings.
   *
   * Heuristic:
   *   - Words >= 420ms OR ending in sentence-terminal punctuation → emphasis.
   *   - Alternate the emphasis side so it feels natural.
   *   - Enforce a 1.5s minimum gap between scheduled emphasis gestures.
   *   - Pauses > 550ms between words → subtle head nod.
   *
   * Timings are in milliseconds relative to audio start. Intentionally NO
   * "both arms open" welcome gesture — the avatar starts from a calm rest
   * pose and only lifts a hand on genuinely emphatic words.
   */
  function buildGestureTrack(words, wtimes, wdurations) {
    if (!words || !words.length) return [];
    const MIN_GAP_MS = 1500;
    const LONG_WORD_MS = 420;
    const track = [];
    let side = 'right';
    let lastEmphasisEnd = -Infinity;

    for (let i = 0; i < words.length; i++) {
      const w = String(words[i] || '').trim();
      const start = wtimes[i];
      const dur = wdurations[i];
      if (start === undefined || dur === undefined) continue;

      const isLong = dur >= LONG_WORD_MS;
      const isTerminal = /[.!?]$/.test(w);
      const canSchedule = (start - lastEmphasisEnd) >= MIN_GAP_MS;

      if ((isLong || isTerminal) && canSchedule) {
        const end = start + Math.max(500, dur * 1.25);
        track.push({ start, end, type: 'emphasis', side });
        lastEmphasisEnd = end;
        side = side === 'right' ? 'left' : 'right';
      }

      if (i < words.length - 1) {
        const nextStart = wtimes[i + 1];
        if (nextStart - (start + dur) > 550) {
          track.push({
            start: start + dur,
            end: nextStart,
            type: 'nod',
          });
        }
      }
    }
    return track;
  }

  useImperativeHandle(ref, () => ({
    /**
     * Play the supplied base64 mp3 and drive mouth morphs + body gestures
     * from phoneme visemes + word-level timings.
     */
    async speak({ audio_base64, visemes, vtimes, vdurations, words, wtimes, wdurations }) {
      if (!audio_base64) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioCtx = audioCtxRef.current;
      if (audioCtx.state === 'suspended') {
        try { await audioCtx.resume(); } catch {}
      }
      // Stop any currently playing source
      try {
        if (currentSourceRef.current) currentSourceRef.current.stop();
      } catch {}

      const buf = await decodeBase64Mp3(audioCtx, audio_base64);
      const src = audioCtx.createBufferSource();
      src.buffer = buf;

      // Analyser for secondary amplitude modulation
      let analyser = analyserRef.current;
      if (!analyser) {
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.6;
        analyser._buf = new Uint8Array(analyser.fftSize);
        analyserRef.current = analyser;
      }

      src.connect(analyser);
      analyser.connect(audioCtx.destination);

      // Rescale viseme timings to match decoded audio duration (our estimates
      // from text length are approximate; the actual mp3 may be shorter/longer).
      let scaledVtimes = vtimes || [];
      let scaledVdurations = vdurations || [];
      if (scaledVtimes.length && buf.duration) {
        const last = scaledVtimes[scaledVtimes.length - 1] + scaledVdurations[scaledVdurations.length - 1];
        const actualMs = buf.duration * 1000;
        if (last > 0) {
          const scale = actualMs / last;
          scaledVtimes = scaledVtimes.map((v) => v * scale);
          scaledVdurations = scaledVdurations.map((v) => v * scale);
        }
      }
      visemeTrackRef.current = {
        visemes: visemes || [],
        vtimes: scaledVtimes,
        vdurations: scaledVdurations,
      };

      // Build speech-driven gesture track from word timings.
      gestureTrackRef.current = buildGestureTrack(words, wtimes, wdurations);

      currentSourceRef.current = src;
      speakingRef.current = true;
      // Record the audio-clock start so the animation loop can align visemes.
      audioStartTimeRef.current = audioCtx.currentTime;
      src.start();

      return new Promise((resolve) => {
        src.onended = () => {
          speakingRef.current = false;
          visemeTrackRef.current = null;
          gestureTrackRef.current = null;
          if (currentSourceRef.current === src) currentSourceRef.current = null;
          resolve();
        };
      });
    },

    stop() {
      try {
        if (currentSourceRef.current) currentSourceRef.current.stop();
      } catch {}
      speakingRef.current = false;
      visemeTrackRef.current = null;
      gestureTrackRef.current = null;
      currentSourceRef.current = null;
    },

    isReady() {
      return status === 'ready';
    },
  }));

  return (
    <div
      data-testid="talking-avatar"
      style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor,
      }}
    >
      <div
        ref={containerRef}
        data-testid="talking-avatar-canvas-host"
        style={{ position: 'absolute', inset: 0 }}
      />
      {status !== 'ready' && (
        <div
          data-testid="talking-avatar-status"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.75)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.3,
            background:
              status === 'error'
                ? 'rgba(0,0,0,0.4)'
                : 'linear-gradient(135deg, rgba(107,142,35,0.18), rgba(26,26,46,0.6))',
          }}
        >
          {status === 'error'
            ? 'Could not load 3D avatar'
            : `Warming up the 3D avatar… ${progress || ''}${progress ? '%' : ''}`}
        </div>
      )}
    </div>
  );
});

export default TalkingAvatar;
